<template>
	<figure :class="['c-image-fader', containerClass]" :style="cssVariables">
		<img
			v-for="(slide, index) in slides"
			:key="slide.src || index"
			:ref="(el) => setImageRef(el, index)"
			:class="[
				'c-image-fader__img',
				imgClass,
				index === activeIndex ? 'is-active' : '',
			]"
			:alt="index === activeIndex ? slide.alt || '' : ''"
			:aria-hidden="index === activeIndex ? 'false' : 'true'"
			:width="width"
			:height="height"
			data-testid="image-fader"
			decoding="async"
			:loading="index === activeIndex ? 'eager' : 'lazy'"
			:fetchpriority="index === activeIndex ? 'high' : 'low'"
		/>
	</figure>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

type Slide = {
	src: string;
	srcset?: string;
	sizes?: string;
	alt?: string;
};

const props = withDefaults(
	defineProps<{
		slides: Slide[];
		currentIndex: number;
		transitionMs?: number;
		width?: number | string;
		height?: number | string;
		containerClass?: string;
		imgClass?: string;
		delayMs?: number;
		preload?: 'none' | 'all' | 'next';
	}>(),
	{
		transitionMs: 300,
		containerClass: '',
		imgClass: '',
		delayMs: 0,
		preload: 'all',
	}
);

const imageElements = ref<(HTMLImageElement | null)[]>([]);

function setImageRef(
	el: Element | import('vue').ComponentPublicInstance | null,
	index: number
) {
	imageElements.value[index] = el instanceof HTMLImageElement ? el : null;
}

const activeIndex = ref(0);
const isAborted = ref(false);

onBeforeUnmount(() => {
	isAborted.value = true;
});

const cssVariables = computed(() => ({
	'--fade-duration': `${props.transitionMs}ms`,
	'--fade-delay': `${props.delayMs}ms`,
}));

function setImageAttributes(image: HTMLImageElement, slide: Slide) {
	if (slide.srcset) image.srcset = slide.srcset;
	else image.removeAttribute('srcset');
	if (slide.sizes) image.sizes = slide.sizes;
	else image.removeAttribute('sizes');
	image.src = slide.src;
}

async function decodeImage(image: HTMLImageElement) {
	try {
		await image.decode?.();
	} catch {
		// no-op: some browsers may throw on decode; onload will already have fired
		return;
	}
}

function waitForIdle() {
	return new Promise<void>((resolve) => {
		const w = window as unknown as {
			requestIdleCallback?: (
				cb: IdleRequestCallback,
				options?: IdleRequestOptions
			) => number;
		};
		if (typeof w.requestIdleCallback === 'function') {
			w.requestIdleCallback(() => resolve(), { timeout: 1200 });
		} else {
			setTimeout(resolve, 50);
		}
	});
}

function shouldPreloadAll() {
	const n = navigator as unknown as {
		connection?: { saveData?: boolean; effectiveType?: string };
	};
	const isSlow =
		!!n.connection?.effectiveType &&
		/(^|-)2g$/.test(n.connection.effectiveType);
	return !n.connection?.saveData && !isSlow;
}

async function preloadSlideAtIndex(index: number) {
	if (isAborted.value) return;
	const image = imageElements.value[index];
	if (!image) return;
	const slide = props.slides[index];
	if (image.currentSrc || image.getAttribute('data-primed') === '1') return;
	setImageAttributes(image, slide);
	await decodeImage(image);
	image.setAttribute('data-primed', '1');
}

async function preloadAllSlidesSequential() {
	if (!shouldPreloadAll() || props.slides.length === 0) return;
	const count = props.slides.length;
	const order = Array.from({ length: count }, (_, i) => i);
	const start = Math.min(Math.max(0, props.currentIndex), count - 1);
	const sequence = [start, ...order.filter((i) => i !== start)];
	for (const idx of sequence) {
		if (isAborted.value) break;
		await waitForIdle();
		await preloadSlideAtIndex(idx);
	}
}

async function preloadNextSlide() {
	if (props.slides.length < 2) return;
	const nextIndex = (props.currentIndex + 1) % props.slides.length;
	await waitForIdle();
	await preloadSlideAtIndex(nextIndex);
}

async function ensureSlideReady(index: number) {
	const image = imageElements.value[index];
	if (!image) return;
	const slide = props.slides[index];
	if (!image.currentSrc) setImageAttributes(image, slide);
	await decodeImage(image);
}

async function showSlideAtIndex(index: number) {
	await ensureSlideReady(index);
	activeIndex.value = index;
}

onMounted(async () => {
	if (props.slides.length === 0) return;

	activeIndex.value = Math.min(
		Math.max(0, props.currentIndex),
		props.slides.length - 1
	);

	const currentImage = imageElements.value[activeIndex.value];
	if (currentImage) {
		setImageAttributes(currentImage, props.slides[activeIndex.value]);
		await decodeImage(currentImage);
	}

	if (props.preload === 'all') preloadAllSlidesSequential();
	else if (props.preload === 'next') preloadNextSlide();
});

watch(
	() => props.currentIndex,
	(value) => {
		if (props.slides.length === 0) return;
		const normalized =
			((value % props.slides.length) + props.slides.length) %
			props.slides.length;
		if (props.preload === 'next') preloadNextSlide();
		showSlideAtIndex(normalized);
	}
);

watch(
	() => props.slides,
	() => {
		for (const img of imageElements.value) {
			img?.removeAttribute('data-primed');
		}
		for (let i = 0; i < props.slides.length; i++) {
			if (i === activeIndex.value) continue;
			const image = imageElements.value[i];
			if (!image) continue;
			setImageAttributes(image, props.slides[i]);
		}
	},
	{ deep: true }
);
</script>

<style scoped lang="scss">
.c-image-fader {
	position: relative;
}

.c-image-fader__img {
	position: absolute;
	inset: 0;
	inline-size: 100%;
	block-size: 100%;
	display: block;
	opacity: 0;
	pointer-events: none;
	transition: opacity var(--fade-duration) ease;
	transition-delay: var(--fade-delay);
	z-index: 0;
}

.c-image-fader__img.is-active {
	opacity: 1;
	z-index: 10;
}

@media (prefers-reduced-motion: reduce) {
	.c-image-fader__img {
		transition: none;
	}
}
</style>
